import type { CSSProperties } from 'react';
import type { CaseStudy } from '../data/types';

const markColors: Record<string, string> = {
  mcdonalds: '#e0a62f',
  tsmc: '#55a08c',
  amazon: '#d99a4a',
  nokia: '#8d9388',
  wework: '#c7a76e',
  ftx: '#d57c68',
  'crisis-2008': '#d36f58',
  container: '#3e8e80',
  'standard-oil': '#b9893e',
  uber: '#a28cc3',
  'ai-coding': '#69b7a5',
  mixue: '#d86650',
  stagflation: '#b38a3f',
  opec: '#9578ac',
  'asian-crisis': '#b8873c',
  transistor: '#4e9b8a',
  'hatch-waxman': '#a27b9e',
  shenzhen: '#73835e',
  'taobao-village': '#d06b4f',
};

function MarkGlyph({ id, color }: { id: string; color: string }) {
  switch (id) {
    case 'mcdonalds':
      return (
        <g>
          <path
            d="M56 154V103c0-31 20-53 49-53s49 22 49 53v51M151 154V103c0-31 20-53 49-53s49 22 49 53v51"
            fill="none"
            stroke={color}
            strokeWidth="15"
            strokeLinecap="round"
          />
          <path d="M30 173h196" stroke="currentColor" strokeWidth="1.5" opacity="0.26" />
          <text x="128" y="198" textAnchor="middle" fill="currentColor" opacity="0.55" fontSize="10" letterSpacing="4">
            OPEN SINCE 1955
          </text>
        </g>
      );
    case 'tsmc':
      return (
        <g>
          <circle cx="132" cy="118" r="87" fill="none" stroke={color} strokeWidth="1.5" opacity="0.8" />
          <circle cx="132" cy="118" r="66" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3" />
          <path d="M132 31v174M45 118h174M72 59l120 118M192 59 72 177" stroke="currentColor" strokeWidth="1" opacity="0.18" />
          <rect x="101" y="87" width="62" height="62" rx="5" fill={color} opacity="0.12" stroke={color} />
          <rect x="113" y="99" width="18" height="18" fill="none" stroke={color} opacity="0.8" />
          <rect x="137" y="99" width="14" height="14" fill={color} opacity="0.4" />
          <path d="M113 139h36M113 146h24" stroke={color} strokeWidth="2" />
          <text x="132" y="207" textAnchor="middle" fill="currentColor" opacity="0.58" fontSize="10" letterSpacing="4">
            PURE-PLAY FOUNDRY
          </text>
        </g>
      );
    case 'amazon':
      return (
        <g>
          <text x="121" y="150" textAnchor="middle" fill="currentColor" fontFamily="Georgia, serif" fontSize="138" fontWeight="500">
            a
          </text>
          <path d="M52 164c37 27 111 30 151 1" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" />
          <path d="m187 158 18 6-13 13" fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <text x="128" y="211" textAnchor="middle" fill="currentColor" opacity="0.52" fontSize="9" letterSpacing="4">
            SELL EVERYTHING
          </text>
        </g>
      );
    case 'nokia':
      return (
        <g>
          <path d="M56 168V66l68 79V66h60v102" fill="none" stroke="currentColor" strokeWidth="12" strokeLinejoin="round" />
          <path d="M73 191c32 11 68 11 108 0" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
          <circle cx="56" cy="58" r="5" fill={color} />
          <circle cx="188" cy="58" r="5" fill={color} />
          <text x="128" y="218" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="5">
            CONNECTING PEOPLE
          </text>
        </g>
      );
    case 'wework':
      return (
        <g>
          <text x="118" y="147" textAnchor="middle" fill="currentColor" fontFamily="Georgia, serif" fontSize="105" fontStyle="italic">
            we
          </text>
          <path d="M50 164h160" stroke={color} strokeWidth="4" />
          <text x="128" y="194" textAnchor="middle" fill="currentColor" opacity="0.55" fontSize="10" letterSpacing="6">
            SPACE AS SERVICE
          </text>
          <circle cx="201" cy="78" r="12" fill="none" stroke={color} opacity="0.7" />
        </g>
      );
    case 'ftx':
      return (
        <g>
          <text x="128" y="146" textAnchor="middle" fill="currentColor" fontSize="88" fontWeight="800" letterSpacing="-2">
            FTX
          </text>
          <path d="M58 73l126 99" stroke={color} strokeWidth="2" strokeDasharray="5 4" opacity="0.85" />
          <path d="M45 181h166" stroke={color} strokeWidth="3" opacity="0.65" />
          <text x="128" y="209" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            TRUST, UNCOLLATERALIZED
          </text>
        </g>
      );
    case 'crisis-2008':
      return (
        <g>
          <path d="M43 72h165M43 116h165M43 160h165" stroke="currentColor" strokeWidth="1" opacity="0.1" />
          <path d="M50 62c26 2 35 18 47 41 14 27 24 12 37 28 12 16 20 40 47 43" fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round" />
          <circle cx="50" cy="62" r="5" fill={color} />
          <circle cx="97" cy="103" r="5" fill={color} />
          <circle cx="134" cy="131" r="5" fill={color} />
          <circle cx="181" cy="174" r="6" fill={color} />
          <text x="128" y="214" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="10" letterSpacing="4">
            THE GREAT RECESSION
          </text>
        </g>
      );
    case 'container':
      return (
        <g>
          <path d="m48 91 95-47 50 27-96 48-49-28Z" fill={color} opacity="0.14" stroke={color} strokeWidth="1.4" />
          <path d="m48 91 49 28v73l-49-29V91ZM97 119l96-48v71l-96 49v-72Z" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.7" />
          <path d="M63 99v66M77 107v66M91 114v66M168 78v66M153 85v65M138 92v66" stroke="currentColor" strokeWidth="1.4" opacity="0.32" />
          <path d="M106 127v71l80-41" fill="none" stroke={color} strokeWidth="2" />
          <text x="128" y="218" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            STANDARD INTERFACE
          </text>
        </g>
      );
    case 'standard-oil':
      return (
        <g>
          <path d="M102 180h52M111 180l11-103h12l12 103M118 135h20M114 159h28M121 77l7-25 8 25" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
          <path d="M98 65h52M118 65l-8-19M140 65l8-19M80 180h96" fill="none" stroke="currentColor" strokeWidth="1.3" opacity="0.4" />
          <path d="M146 83c0 9 5 12 5 20a9 9 0 1 1-18 0c0-8 9-13 13-20Z" fill={color} opacity="0.8" />
          <text x="128" y="211" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            REFINE &amp; CONTROL
          </text>
        </g>
      );
    case 'uber':
      return (
        <g>
          <path d="M83 55v77c0 48 22 70 45 70s45-22 45-70V55" fill="none" stroke="currentColor" strokeWidth="16" strokeLinecap="round" />
          <path d="M47 151c32 23 112 32 151-7" fill="none" stroke={color} strokeWidth="2" strokeDasharray="4 5" />
          <circle cx="47" cy="151" r="5" fill={color} />
          <circle cx="198" cy="144" r="5" fill={color} />
          <text x="128" y="213" textAnchor="middle" fill="currentColor" opacity="0.52" fontSize="9" letterSpacing="5">
            MOVE ON DEMAND
          </text>
        </g>
      );
    case 'ai-coding':
      return (
        <g>
          <path d="M89 51C57 76 57 93 57 116s0 39 32 65M151 51c32 25 32 42 32 65s0 39-32 65" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
          <path d="M101 94 85 113l16 18M139 94l16 19-16 18M121 87l-10 52" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m178 70 4 11 11 4-11 4-4 11-4-11-11-4 11-4 4-11Z" fill={color} />
          <text x="128" y="210" textAnchor="middle" fill="currentColor" opacity="0.52" fontSize="9" letterSpacing="4">
            GENERATE → VERIFY
          </text>
        </g>
      );
    case 'mixue':
      return (
        <g>
          <path d="M128 38v108M82 66l92 52M174 66l-92 52M82 170l92-52M174 170l-92-52" stroke={color} strokeWidth="3" strokeLinecap="round" />
          <circle cx="128" cy="118" r="12" fill="none" stroke={color} strokeWidth="3" />
          <circle cx="128" cy="38" r="4" fill={color} />
          <circle cx="82" cy="66" r="4" fill={color} />
          <circle cx="174" cy="66" r="4" fill={color} />
          <circle cx="82" cy="170" r="4" fill={color} />
          <circle cx="174" cy="170" r="4" fill={color} />
          <text x="128" y="211" textAnchor="middle" fill="currentColor" opacity="0.55" fontSize="9" letterSpacing="5">
            MIXUE
          </text>
        </g>
      );
    case 'stagflation':
      return (
        <g>
          <path d="M38 64h180M38 112h180M38 160h180" stroke="currentColor" strokeWidth="1" opacity="0.1" />
          <path d="M44 164c24-4 31-25 44-52 10-22 20-12 31 8 14 25 23 8 35-22 13-32 24-43 46-48"
            fill="none" stroke={color} strokeWidth="3.2" strokeLinecap="round" />
          <circle cx="194" cy="52" r="8" fill={color} opacity="0.25" />
          <text x="194" y="57" textAnchor="middle" fill={color} fontSize="12" fontWeight="700">%</text>
          <path d="M44 179h176" stroke="currentColor" strokeWidth="1.2" opacity="0.3" />
          <text x="132" y="204" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            1965 — 1983
          </text>
        </g>
      );
    case 'opec':
      return (
        <g>
          <path d="M82 174h100M91 174l12-87h56l13 87M98 130h76M104 93l18-38 18 38" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
          <path d="M132 55v-18M124 37l8-9 8 9" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="76" cy="188" r="4" fill={color} />
          <circle cx="104" cy="196" r="4" fill={color} opacity="0.7" />
          <circle cx="132" cy="200" r="4" fill={color} opacity="0.55" />
          <circle cx="160" cy="196" r="4" fill={color} opacity="0.7" />
          <circle cx="188" cy="188" r="4" fill={color} />
          <text x="132" y="222" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            QUOTA / BARGAIN
          </text>
        </g>
      );
    case 'asian-crisis':
      return (
        <g>
          <path d="M38 178h184M38 132h184M38 86h184" stroke="currentColor" strokeWidth="1" opacity="0.1" />
          <path d="M46 64c31 22 52 15 75 43 22 27 44 25 75-8 13-15 22-23 35-24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
          <circle cx="46" cy="64" r="5" fill={color} />
          <circle cx="121" cy="107" r="5" fill={color} />
          <circle cx="196" cy="99" r="6" fill={color} />
          <path d="m185 112 11-13 11 13" fill="none" stroke={color} strokeWidth="2" />
          <text x="132" y="207" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            1997 / FIXED PEG
          </text>
        </g>
      );
    case 'transistor':
      return (
        <g>
          <circle cx="132" cy="112" r="66" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.18" />
          <path d="M88 73v78M88 112h46M88 91l32-23M88 133l32 23" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
          <path d="M134 112h38M172 112v-29M172 141v-29" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          <circle cx="88" cy="112" r="5" fill={color} />
          <circle cx="172" cy="83" r="4" fill={color} opacity="0.7" />
          <circle cx="172" cy="141" r="4" fill={color} opacity="0.7" />
          <path d="M46 112h20M198 112h20" stroke={color} strokeWidth="2" opacity="0.55" />
          <text x="132" y="207" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            BELL LABS / 1947
          </text>
        </g>
      );
    case 'hatch-waxman':
      return (
        <g>
          <rect x="72" y="81" width="112" height="70" rx="35" fill="none" stroke={color} strokeWidth="3" transform="rotate(-25 128 116)" />
          <path d="M128 81v70" stroke={color} strokeWidth="2" opacity="0.7" transform="rotate(-25 128 116)" />
          <path d="M74 54c28-16 80-16 108 0M74 183c28 16 80 16 108 0" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.25" />
          <text x="132" y="207" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            PATENT → GENERIC
          </text>
        </g>
      );
    case 'shenzhen':
      return (
        <g>
          <path d="M44 178h176M60 178V93h30v85M101 178V69h37v109M149 178V108h29v70M187 178V81h22v97"
            fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
          <path d="M60 78h30M101 57h37M149 93h29M187 67h22" stroke={color} strokeWidth="3" />
          <path d="M51 194h158" stroke={color} strokeWidth="1.4" opacity="0.55" />
          <circle cx="75" cy="116" r="2.5" fill={color} opacity="0.65" />
          <circle cx="119" cy="100" r="2.5" fill={color} opacity="0.65" />
          <circle cx="164" cy="132" r="2.5" fill={color} opacity="0.65" />
          <text x="132" y="218" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            SEZ / 1980
          </text>
        </g>
      );
    case 'taobao-village':
      return (
        <g>
          <path d="m56 117 72-53 72 53" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
          <path d="M72 117v65h112v-65M102 182v-42h40v42" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinejoin="round" />
          <path d="M148 92h36l-7 47h-29l-7-47M158 92V80h18v12" fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
          <circle cx="75" cy="155" r="3" fill={color} />
          <circle cx="127" cy="198" r="3" fill={color} opacity="0.6" />
          <circle cx="182" cy="145" r="3" fill={color} opacity="0.6" />
          <text x="132" y="224" textAnchor="middle" fill="currentColor" opacity="0.5" fontSize="9" letterSpacing="4">
            PLATFORM / VILLAGE
          </text>
        </g>
      );
    default:
      return <circle cx="120" cy="120" r="50" fill="none" stroke={color} />;
  }
}

export function CaseMark({
  study,
  className = '',
  decorative = false,
  light = false,
}: {
  study: CaseStudy;
  className?: string;
  decorative?: boolean;
  light?: boolean;
}) {
  const color = markColors[study.id] ?? study.accent;
  return (
    <svg
      className={`case-mark ${className}`}
      viewBox="0 0 256 240"
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : `${study.title} 标志`}
      style={{ color: light ? '#f3eddf' : 'var(--ink)', '--mark-accent': color } as CSSProperties}
    >
      <MarkGlyph id={study.id} color={color} />
    </svg>
  );
}
